import { Dispatch, SetStateAction } from "react";
import { ProductCost } from "../../../types/product-cost.type";
import { formatNumber } from "../../../utils";
import { formatDateTimeBkk } from "../../../utils/date";
import CataloguePagination from "../shared/Pagination";
import {
  CATALOGUE_CELL_PADDING,
  STRIPE_BACKGROUND,
  catalogueRowHeight,
  formatCataloguePrice,
} from "../shared/constants";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Theme } from "@mui/material/styles";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Skeleton from "../../ReprintAuth/Skeleton";

const READ_COLUMNS = [
  "ลำดับสินค้า",
  "รหัสสินค้า",
  "ชื่อสินค้า",
  "หน่วย",
  "ราคาหน้าซอง",
  "ต้นทุนต่อหน่วย",
  "% ของหน้าซอง",
  "แก้ไขเมื่อ",
];
const EDIT_COLUMNS = [...READ_COLUMNS, "การดำเนินการ"];

/** A body2 date line over a caption name line, each rendered 20px tall. */
const UPDATED_CELL_HEIGHT = 40;

/** The two-line แก้ไขเมื่อ cell is the tallest thing in a row, so it sets the row height. */
const ROW_HEIGHT = catalogueRowHeight(UPDATED_CELL_HEIGHT);

/** The reports leave a product without a cost out of their margins, so the gap is called out. */
const NO_COST_LABEL = "ไม่มีต้นทุน";
const PERCENT_SCALE = 100;
const SHARE_DECIMALS = 1;

/** The cost as a % of the face price, or "-" when either is missing (or the face price is 0). */
const formatFacePriceShare = (
  cost: number | null,
  facePrice: number | null,
): string =>
  cost === null || !facePrice
    ? "-"
    : `${formatNumber((cost * PERCENT_SCALE) / facePrice, { decimals: SHARE_DECIMALS })}%`;

const headCellSx = {
  borderRight: (theme: Theme) => `1px solid ${theme.palette.draft.main}`,
  "&:last-of-type": { borderRight: "none" },
};

interface ProductCostTableProps {
  productCosts: ProductCost[];
  loading: boolean;
  totalCount: number;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  limit: number;
  setLimit: Dispatch<SetStateAction<number>>;
  /** False for the roles that read costs without maintaining them: no edit column. */
  canEdit: boolean;
  onEdit: (productCost: ProductCost) => void;
}

export default function ProductCostTable({
  productCosts,
  loading,
  totalCount,
  page,
  setPage,
  limit,
  setLimit,
  canEdit,
  onEdit,
}: ProductCostTableProps) {
  const columns = canEdit ? EDIT_COLUMNS : READ_COLUMNS;

  const handleClickEdit = (productCost: ProductCost) => () => {
    onEdit(productCost);
  };

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead
            sx={{ bgcolor: (theme) => theme.palette.background.default }}
          >
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column} sx={headCellSx}>
                  {column}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <Skeleton
                columns={columns.length}
                rows={limit}
                rowHeight={ROW_HEIGHT}
              />
            )}
            {!loading && productCosts.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  align="center"
                  sx={{ py: 5 }}
                >
                  <Typography color="text.secondary" fontWeight={500}>
                    ไม่พบข้อมูลสินค้า
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              productCosts.map((productCost, index) => (
                <TableRow
                  key={productCost.product_code}
                  sx={{
                    bgcolor: index % 2 === 1 ? STRIPE_BACKGROUND : "#fff",
                    height: ROW_HEIGHT,
                    "& td, & th": { padding: `${CATALOGUE_CELL_PADDING}px` },
                  }}
                >
                  <TableCell align="center">
                    {productCost.sequence ?? "—"}
                  </TableCell>
                  <TableCell>{productCost.product_code}</TableCell>
                  <TableCell>{productCost.product_name ?? "-"}</TableCell>
                  <TableCell>{productCost.unit ?? "-"}</TableCell>
                  <TableCell align="right">
                    {formatCataloguePrice(productCost.face_price)}
                  </TableCell>
                  <TableCell align="right">
                    {productCost.cost === null ? (
                      <Chip
                        label={NO_COST_LABEL}
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    ) : (
                      `${formatNumber(productCost.cost)} บาท`
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {formatFacePriceShare(
                      productCost.cost,
                      productCost.face_price,
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDateTimeBkk(productCost.updated_at) || "-"}
                    </Typography>
                    {productCost.updated_by_name && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        โดย {productCost.updated_by_name}
                      </Typography>
                    )}
                  </TableCell>
                  {canEdit && (
                    <TableCell>
                      <Tooltip title="แก้ไขต้นทุน">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={handleClickEdit(productCost)}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <CataloguePagination
        totalCount={totalCount}
        page={page}
        setPage={setPage}
        limit={limit}
        setLimit={setLimit}
      />
    </Paper>
  );
}
